import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { CreditCard, Loader2 } from "lucide-react";
import UnauthorizedPlanModal from "./UnauthorizedPlanModal";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

import { supabase } from "@/lib/supabase";

interface WompiButtonProps {
  amount: number;
  planId?: string;
  planName: string;
  buttonText?: string;
  className?: string;
  onBeforeOpen?: () => void;
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}

const WOMPI_CHECKOUT_URL = "https://checkout.wompi.co/p/";

const getWompiPublicKey = () => import.meta.env.VITE_WOMPI_PUBLIC_KEY || "";

const getWompiRedirectUrl = () => {
  const configuredUrl = import.meta.env.VITE_WOMPI_REDIRECT_URL?.trim();

  if (configuredUrl) {
    return configuredUrl;
  }

  if (typeof window !== "undefined" && window.location?.origin) {
    return `${window.location.origin}/payment/result`;
  }

  return undefined;
};

const createIntegritySignature = async (
  reference: string,
  amountInCents: number,
  currency: string
) => {
  const integritySecret = import.meta.env.VITE_WOMPI_INTEGRITY_SECRET;

  if (!integritySecret) {
    return undefined;
  }

  const payload = `${reference}${amountInCents}${currency}${integritySecret}`;
  const encodedPayload = new TextEncoder().encode(payload);
  const hashBuffer = await crypto.subtle.digest("SHA-256", encodedPayload);

  return Array.from(new Uint8Array(hashBuffer))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
};

const normalizeReferencePart = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]/g, "_")
    .replace(/_+/g, "_")
    .toUpperCase();

declare global {
  interface Window {
    WidgetCheckout: any;
  }
}

const loadWompiWidgetScript = (publicKey: string): Promise<any> => {
  return new Promise((resolve, reject) => {
    if (window.WidgetCheckout) {
      resolve(window.WidgetCheckout);
      return;
    }
    const existing = document.getElementById("wompi-widget-script-src");
    if (existing) {
      existing.addEventListener("load", () => resolve(window.WidgetCheckout));
      existing.addEventListener("error", () => reject(new Error("Error al cargar Wompi Widget")));
      return;
    }
    const script = document.createElement("script");
    script.id = "wompi-widget-script-src";
    script.src = "https://checkout.wompi.co/widget.js";
    if (publicKey) {
      script.setAttribute("data-public-key", publicKey);
    }
    script.async = true;
    script.onload = () => resolve(window.WidgetCheckout);
    script.onerror = () => reject(new Error("Error al cargar el widget de Wompi."));
    document.head.appendChild(script);
  });
};

const WompiButton: React.FC<WompiButtonProps> = ({
  amount,
  planId,
  planName,
  buttonText = "Pagar con Wompi",
  className,
  onBeforeOpen,
  onSuccess,
  onError,
}) => {
  const { user } = useAuth();
  const [showUnauthorizedModal, setShowUnauthorizedModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const stopLoading = () => {
    if (isMountedRef.current) {
      setIsLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!user) {
      setShowUnauthorizedModal(true);
      return;
    }

    const publicKey = getWompiPublicKey();

    if (!publicKey) {
      toast.error("Falta configurar la llave pública de Wompi");
      onError?.(new Error("Missing Wompi public key"));
      return;
    }

    setIsLoading(true);

    try {
      const currency = "COP";
      const amountInCents = Math.round(amount * 100);
      const planPrefix = planId
        ? planId.toUpperCase()
        : normalizeReferencePart(planName);
      const userPart = user.id.split("-")[0];
      const redirectUrl = getWompiRedirectUrl();

      // 1. Pre-iniciar vía backend API (/payments/wompi/initiate) con timeout estricto de 2.5s
      let apiInitiated = false;
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:8001";
      var finalReference: string | undefined;
      var finalSignature: string | undefined;
      var finalPublicKey: string | undefined;

      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData?.session?.access_token;

        if (token) {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 2500); // 2.5s max wait

          const res = await fetch(`${apiBaseUrl}/api/v1/payments/wompi/initiate`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              amount_in_cents: amountInCents,
              plan_id: planPrefix,
              currency,
              redirect_url: redirectUrl,
            }),
            signal: controller.signal,
          });

          clearTimeout(timeoutId);

          if (res.ok) {
            const initResult = await res.json();
            if (initResult.reference && initResult.signature) {
              apiInitiated = true;
              finalReference = initResult.reference;
              finalSignature = initResult.signature;
              if (initResult.public_key) {
                finalPublicKey = initResult.public_key;
              }
            }
          } else {
            console.warn("Backend initiate_wompi_payment returned non-200:", res.status);
          }
        }
      } catch (apiErr) {
        console.warn("Backend initiate_wompi_payment unreachable or timed out (2.5s), using instant fallback:", apiErr);
      }

      const activePublicKey = finalPublicKey || publicKey;
      const reference = (typeof finalReference !== "undefined") ? finalReference : `${planPrefix}_${userPart}_${Date.now()}`;
      const integrity = (typeof finalSignature !== "undefined") ? finalSignature : await createIntegritySignature(
        reference,
        amountInCents,
        currency
      );

      if (!integrity) {
        throw new Error("Missing Wompi integrity signature");
      }

      // 2. Pre-insertar en DB si no se usó API
      if (!apiInitiated) {
        const { error: dbError } = await supabase.from("wompi_transactions").insert({
          user_id: user.id,
          reference,
          amount_in_cents: amountInCents,
          currency,
          redirect_url: redirectUrl,
          status: "PENDING",
        });

        if (dbError) {
          console.error("Supabase error pre-inserting wompi_transaction:", dbError);
        }
      }

      const fullName = user.user_metadata?.full_name || user.user_metadata?.name || "";
      const rawPhone = user.user_metadata?.phone || user.user_metadata?.phone_number || user.phone || "";
      const cleanPhone = String(rawPhone).replace(/\D/g, "");
      const validPhone = cleanPhone.length === 10 ? cleanPhone : undefined;

      // Desenfocar elemento activo para evitar advertencias aria-hidden en modales
      if (document.activeElement && typeof (document.activeElement as HTMLElement).blur === "function") {
        (document.activeElement as HTMLElement).blur();
      }

      onBeforeOpen?.();

      // Cargar script del Widget embebido de Wompi pasando data-public-key
      const WidgetCheckout = await loadWompiWidgetScript(activePublicKey);

      if (!WidgetCheckout) {
        throw new Error("WidgetCheckout de Wompi no está disponible");
      }

      // Configurar el modal WidgetCheckout
      const checkoutConfig: any = {
        currency,
        amountInCents,
        reference,
        publicKey: activePublicKey,
        signature: {
          integrity,
        },
      };

      if (redirectUrl) {
        checkoutConfig.redirectUrl = redirectUrl;
      }

      // Sanitizar datos del cliente (evita enviar teléfonos inválidos que deshabilitan botones del widget)
      const customerData: Record<string, any> = {};
      if (user.email) customerData.email = user.email;
      if (fullName && fullName.trim().length > 0) customerData.fullName = fullName.trim();
      if (validPhone) {
        customerData.phoneNumber = validPhone;
        customerData.phoneNumberPrefix = "+57";
      }

      if (Object.keys(customerData).length > 0) {
        checkoutConfig.customerData = customerData;
      }


      const checkout = new WidgetCheckout(checkoutConfig);
      stopLoading();

      // Abrir el Modal Embebido en la misma página
      checkout.open((result: any) => {
        const transaction = result?.transaction;

        if (transaction?.status === "APPROVED") {
          toast.success("¡Pago aprobado con éxito!");
          onSuccess?.();
          if (redirectUrl) {
            window.location.href = `${redirectUrl}?payment=approved&ref=${encodeURIComponent(reference)}`;
          } else {
            window.location.href = `/dashboard?payment=approved&ref=${encodeURIComponent(reference)}`;
          }
        } else if (transaction?.status === "DECLINED" || transaction?.status === "ERROR") {
          const msg = transaction?.status_message || "La transacción no fue aprobada";
          toast.error(`Pago no completado: ${msg}`);
          onError?.(new Error(msg));
        }
      });

    } catch (error) {
      toast.error("Error al abrir la pasarela de pagos Wompi");
      stopLoading();
      onError?.(error);
    }
  };

  return (
    <>
      <Button
        onClick={handlePayment}
        disabled={isLoading}
        className={cn(
          "w-full rounded-xl bg-legal-blue font-bold text-white shadow-md shadow-legal-blue/20 transition-all hover:bg-indigo-700 active:scale-[0.98] flex items-center justify-center gap-2",
          className
        )}
      >
        {isLoading ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Preparando pago...</span>
          </>
        ) : (
          <>
            <CreditCard className="h-5 w-5" />
            <span>{buttonText}</span>
          </>
        )}
      </Button>

      <UnauthorizedPlanModal
        isOpen={showUnauthorizedModal}
        onClose={() => setShowUnauthorizedModal(false)}
        planName={planName}
        planPrice={amount}
      />
    </>
  );
};

export default WompiButton;
